import { Box, Flex, HStack, Image, Tag, Text } from '@chakra-ui/react';
import {
  NotificationTemplateListItemFragment,
  NotificationTemplateStatus,
  NotificationType,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import * as htmlToText from 'html-to-text';
import { KeyboardEvent, memo, useMemo } from 'react';
import { useNavigate } from 'react-router';

interface NotificationTemplateCardProps {
  template: NotificationTemplateListItemFragment;
  showPartner?: boolean;
}

export const NotificationTemplateCard = memo(function NotificationTemplateCard({
  template,
  showPartner = false,
}: NotificationTemplateCardProps) {
  const navigate = useNavigate();
  const isClickable = Boolean(template.partner);

  const handleClick = () => {
    if (!isClickable) return;
    navigate(
      `/organizations/notifications/partners/${template.partner?.id}/edit/${template.id}`
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const isDraft = template.status === NotificationTemplateStatus.DRAFT;
  const sideLabel =
    template.side === TransactionSide.BUY
      ? 'Buy'
      : template.side === TransactionSide.SELL
        ? 'Sell'
        : null;
  const typeLabel =
    template.notificationType === NotificationType.TEXT
      ? 'Text message'
      : 'Email';

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const cleanedMessageContent = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return htmlToText.htmlToText(template.messageContent, {
      preserveNewlines: false,
    }) as unknown as string;
  }, [template.messageContent]);

  // Truncate message for preview
  const messagePreview =
    cleanedMessageContent.length > 80
      ? `${cleanedMessageContent.substring(0, 80)}...`
      : cleanedMessageContent;

  return (
    <Box
      _hover={{ bg: 'whiteAlpha.100' }}
      aria-disabled={!isClickable}
      bg="whiteAlpha.50"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="lg"
      cursor={isClickable ? 'pointer' : 'default'}
      p={4}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : -1}
      transition="background 0.2s"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {isDraft && (
        <Flex alignItems="center" gap={2} mb={2}>
          <Box bg="orange.400" borderRadius="full" height="6px" width="6px" />
          <Text color="whiteAlpha.700" fontSize="xs">
            Draft
          </Text>
        </Flex>
      )}
      <Text color="white" fontWeight="medium" mb={2}>
        {template.title}
      </Text>
      <HStack flexWrap="wrap" mb={3} spacing={2}>
        {sideLabel && (
          <Tag bg="whiteAlpha.100" color="white" size="sm">
            {sideLabel}
          </Tag>
        )}
        <Tag bg="whiteAlpha.100" color="white" size="sm">
          {typeLabel}
        </Tag>
        {template.recipients.map((recipient) => (
          <Tag key={recipient} bg="whiteAlpha.100" color="white" size="sm">
            {recipient}
          </Tag>
        ))}
      </HStack>
      <Box bg="whiteAlpha.50" borderRadius="sm" p={3}>
        <Text
          color="whiteAlpha.700"
          dangerouslySetInnerHTML={{ __html: messagePreview }}
          fontSize="sm"
        />
      </Box>
      {showPartner && template.partner && (
        <Flex alignItems="center" gap={2} mt={3}>
          {template.partner.logo?.url && (
            <Box bg="white" borderRadius="sm" p={1}>
              <Image
                alt={template.partner.name}
                height="20px"
                objectFit="contain"
                src={template.partner.logo.url}
              />
            </Box>
          )}
          <Text color="white" fontSize="sm">
            {template.partner.name}
          </Text>
          {!template.partner.isActive && (
            <Tag bg="whiteAlpha.200" color="whiteAlpha.600" size="sm">
              Inactive
            </Tag>
          )}
        </Flex>
      )}
    </Box>
  );
});
